import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { MODULES } from "../../data/pricingContent";

export function ModuleFeaturesSection({
    eyebrow = "// module features",
    title = "Full feature set",
    titleAccent = "on every plan.",
    description = "Nine modules power six departments. Starter, Business and Enterprise all include every feature below — plans differ only by license count.",
    modules = MODULES,
    showDepartment = true,
    animated = true,
    compareLink,
    testId = "module-features-section",
    className = "",
}) {
    return (
        <section
            className={`relative py-16 lg:py-20 border-t border-white/[0.06] ${className}`}
            data-testid={testId}
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8">
                <div className="max-w-3xl mb-10">
                    <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-3">
                        {eyebrow}
                    </p>
                    <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.05]">
                        {title}
                        {titleAccent ? (
                            <>
                                <br />
                                <span className="text-zinc-500">{titleAccent}</span>
                            </>
                        ) : null}
                    </h2>
                    <p className="mt-4 text-sm text-zinc-500 font-light leading-relaxed">
                        {description}
                    </p>
                    {compareLink && (
                        <Link
                            to={compareLink}
                            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-electric hover:text-white transition"
                        >
                            See competitor comparison
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
                    {modules.map((mod, i) => {
                        const accent = mod.accent || "text-electric";
                        const accentBg =
                            mod.accentBg || "bg-electric/10 border-electric/25";
                        const Card = animated ? motion.div : "div";
                        const cardProps = animated
                            ? {
                                  initial: { opacity: 0, y: 16 },
                                  whileInView: { opacity: 1, y: 0 },
                                  viewport: { once: true, margin: "-40px" },
                                  transition: { duration: 0.4, delay: i * 0.04 },
                              }
                            : {};

                        return (
                            <Card
                                key={mod.id}
                                {...cardProps}
                                className="rounded-2xl border border-white/[0.08] bg-obsidian-200/60 p-6 hover:border-white/15 transition-colors"
                                data-testid={`module-card-${mod.id}`}
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <span
                                        className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border ${accentBg}`}
                                    >
                                        <mod.icon className={`w-4 h-4 ${accent}`} />
                                    </span>
                                    <div className="min-w-0">
                                        <h3 className="font-outfit text-base font-semibold text-white">
                                            {mod.name}
                                        </h3>
                                        {showDepartment && mod.department && (
                                            <p className="text-[10px] text-zinc-600 font-code uppercase tracking-[0.14em] mt-0.5">
                                                {mod.department}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <ul className="grid grid-cols-2 gap-x-3 gap-y-2">
                                    {mod.features.map((f) => (
                                        <li
                                            key={f}
                                            className="flex items-center gap-2 text-[13px] text-zinc-300"
                                        >
                                            <Check
                                                className={`w-3.5 h-3.5 shrink-0 ${accent}`}
                                            />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
